import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });

export default function Trials() {
  const navigate = useNavigate();
  const [trials, setTrials] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSfg, setFilterSfg] = useState('');

  useEffect(() => {
    Promise.all([
      API.get('/trials/all'),
      API.get('/projects'),
    ]).then(([t, p]) => {
      setTrials(t.data);
      setProjects(p.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = trials.filter(t => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    const hay = [t.trial_number, t.product_name, t.product_code, t.project_name, t.project_code].join(' ').toLowerCase();
    const matchSearch = words.length === 0 || words.every(w => hay.includes(w));
    const matchProject = !filterProject || String(t.project_id) === filterProject;
    const matchStatus = !filterStatus || t.status === filterStatus;
    const matchSfg = !filterSfg || (filterSfg === 'sfg' ? t.is_sfg === 1 : t.is_sfg !== 1);
    return matchSearch && matchProject && matchStatus && matchSfg;
  });

  const stats = {
    total: trials.length,
    draft: trials.filter(t => t.status === 'draft' || !t.status).length,
    confirmed: trials.filter(t => t.status === 'confirmed').length,
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Trials</div>
          <div className="page-subtitle">All trials across all projects and products</div>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
        <div className="stat-card" style={{ background: 'linear-gradient(135deg, #7B2D6B, #9d3d8a)' }}>
          <div className="stat-value" style={{ color: '#fff' }}>{stats.total}</div>
          <div className="stat-label" style={{ color: 'rgba(255,255,255,0.8)' }}>Total Trials</div>
        </div>
        <div className="stat-card" style={{ background: '#fff3e0' }}>
          <div className="stat-value" style={{ color: '#e67e00' }}>{stats.draft}</div>
          <div className="stat-label">Draft</div>
        </div>
        <div className="stat-card" style={{ background: '#e8f5e9' }}>
          <div className="stat-value" style={{ color: '#2e7d32' }}>{stats.confirmed}</div>
          <div className="stat-label">Confirmed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="text" placeholder="🔍 Search trial no, product, project..."
            value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 2, minWidth: 200 }} />
          <select value={filterProject} onChange={e => setFilterProject(e.target.value)} style={{ flex: 1, minWidth: 160 }}>
            <option value="">All Projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div style={{ display: 'flex', gap: 4, background: 'var(--bg-elevated)', borderRadius: 8, padding: 3, marginBottom: 8 }}>
            {[['', 'All'], ['fg', 'FG'], ['sfg', 'SFG']].map(([val, label]) => (
              <button key={val} className={'btn btn-sm ' + (filterSfg === val ? 'btn-primary' : 'btn-ghost')}
                onClick={() => setFilterSfg(val)} style={{ padding: '4px 10px', fontSize: 12 }}>{label}</button>
            ))}
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ flex: 1, minWidth: 130 }}>
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="confirmed">Confirmed</option>
          </select>
          {(search || filterProject || filterStatus) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setFilterProject(''); setFilterStatus(''); }}>✕ Clear</button>
          )}
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>Showing {filtered.length} of {trials.length}</span>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div> :
        filtered.length === 0 ? (
          <div className="empty-state"><div className="empty-state-icon">🧪</div><p>No trials found.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Trial No</th>
                  <th>Product</th>
                  <th>Product Code</th>
                  <th>Project</th>
                  <th>Date</th>
                  <th>Materials</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/products/' + t.product_id)}>
                    <td><span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--accent)' }}>T-{String(t.trial_number).padStart(2,'0')}</span></td>
                    <td style={{ fontWeight: 500 }}>{t.product_name}</td>
                    <td><span style={{ fontFamily: 'monospace', fontSize: 11 }}>{t.product_code || '—'}</span></td>
                    <td style={{ fontSize: 12 }}>{t.project_name || '—'}</td>
                    <td style={{ fontSize: 12 }}>{t.date ? new Date(t.date).toLocaleDateString('en-AE') : '—'}</td>
                    <td style={{ textAlign: 'center', fontSize: 12 }}>{t.material_count || 0}</td>
                    <td>
                      <span style={{ background: t.status === 'confirmed' ? '#e8f5e9' : '#fff3e0', color: t.status === 'confirmed' ? '#2e7d32' : '#e67e00', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                        {t.status || 'draft'}
                      </span>
                    </td>
                    <td><button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); navigate('/products/' + t.product_id); }}>View →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
